import { useState, useCallback } from 'react'
import { Check, CopySimple, X } from '@phosphor-icons/react'
import type { QueryClient } from 'react-query'

import { Button } from '@/main/components/Button/Button'
import { Item } from '@/main/features/Item/itemSlice'
import { FormState } from '@/main/features/List/listSlice'
import { formatPhoneNumber } from '@/cart/utils/formatPhoneNumber'

export function SubmissionFormError({
  form,
  items,
  client,
  errorType,
  errorMessage = 'Unknown error',
}: {
  form?: FormState
  items: Item[]
  client: QueryClient
  errorType?: string
  errorMessage?: string
}) {
  const [isCopied, setIsCopied] = useState<boolean>()

  const handleCopyToClipboard = useCallback(async (text: string) => {
    setIsCopied(undefined)

    try {
      await navigator.clipboard.writeText(text)

      setIsCopied(true)
    } catch (err) {
      console.error(err)
      setIsCopied(false)
    }
  }, [])

  const data = items.map((item) => ({
    ...item,
    ...client.getQueryData<AcmeProp>(['item', `${item.postId}`]),
  }))
  const kits = data.filter((item) => item.is_set || item.sku?.includes('-S'))
  const valid = data.filter((item) => item.sku) as Array<Item & AcmeProp>
  const invalid = data.filter((item) => !item.sku)
  const skus = valid.map((item) => `${item.sku} `.repeat(item.count).trim()).join(' ')

  const mailto = 'mailto:props@acmebrooklyn.com'

  let subject = 'Shopping Cart Request (error submitting)'

  if (
    form?.billing?.values?.client &&
    form?.billing?.values?.reference &&
    form?.dates?.values?.startDate &&
    form?.dates?.values?.endDate
  ) {
    subject = `${form?.billing?.values?.client}, ${form?.billing?.values.reference}, ${form?.dates?.values.startDate} - ${form?.dates?.values.endDate}`
  }

  let text = `I am emailing directly because of an error when I tried to submit. Below is my request info.\n\n`

  text += `Name: ${form?.profile?.values.firstName} ${form?.profile?.values.lastName}
Company/Client: ${form?.billing?.values.client}
Email: ${form?.profile?.values.email}
Phone: ${formatPhoneNumber(form?.profile?.values.phone ?? '')}
Job Reference: ${form?.billing?.values.reference}\n`

  if (form?.billing?.values?.cc?.length) {
    text += `Additional Contact (CC): ${form.billing.values.cc}\n`
  }

  if (form?.shipping?.values.shippingMethod === 'delivery') {
    text += `\nI would like a quote for ACME to arrange delivery by courier\n`
  } else {
    text += `\nI will Pick-Up and Return myself to/from ACME (350 Meserole)\n`
  }

  text += `Start Date: ${form?.dates?.values.startDate}
Return/End Date: ${form?.dates?.values.endDate}`

  if (
    form?.shipping?.values.shippingMethod === 'delivery' &&
    form.shipping.values.shippingDetail?.deliveryType !== undefined &&
    form.shipping.values.shippingDetail.deliveryType !== 'returnonly'
  ) {
    if (form.shipping.values.shippingDetail.deliveryType === 'roundtrip') {
      text += `\n\nRound trip delivery to and from:\n`
    } else if (form.shipping.values.shippingDetail.deliveryType === 'deliveryonly') {
      text += `\n\nDelivery only (I will return props to ACME myself):\n`
    } else if (form.shipping.values.shippingDetail.deliveryType === 'multitrip') {
      text += `\n\nDelivery to (return location is different):\n`
    }

    text += `ATTN: ${form.shipping.values.shippingDetail.deliveryContact}, ${form.shipping.values.shippingDetail.deliveryPhone}
${form.shipping.values.shippingDetail.deliveryBusiness}
${form.shipping.values.shippingDetail.deliveryAddress}
${form.shipping.values.shippingDetail.deliveryCity}, NY ${form.shipping.values.shippingDetail.deliveryZip}`

    if (form.shipping.values.shippingDetail.deliveryInstructions) {
      text += `\nNotes/Instructions:
${form.shipping.values.shippingDetail.deliveryInstructions}`
    }
  }

  if (
    form?.shipping?.values.shippingMethod === 'delivery' &&
    form.shipping.values.shippingDetail?.deliveryType !== undefined &&
    (form.shipping.values.shippingDetail.deliveryType === 'returnonly' ||
      form.shipping.values.shippingDetail.deliveryType === 'multitrip')
  ) {
    if (form.shipping.values.shippingDetail.deliveryType === 'returnonly') {
      text += `\n\nReturn only (I will pick-up props from ACME):\n`
    } else if (form.shipping.values.shippingDetail.deliveryType === 'multitrip') {
      text += `\n\nReturn to (delivery location is different):\n`
    }

    text += `ATTN: ${form.shipping.values.shippingDetail.returnContact}, ${form.shipping.values.shippingDetail.returnPhone}
${form.shipping.values.shippingDetail.returnBusiness}
${form.shipping.values.shippingDetail.returnAddress}
${form.shipping.values.shippingDetail.returnCity}, NY ${form.shipping.values.shippingDetail.returnZip}`

    if (form.shipping.values.shippingDetail.returnInstructions) {
      text += `\nNotes/Instructions:
${form.shipping.values.shippingDetail.returnInstructions}`
    }
  }

  if (kits.length) {
    text += `\n\nSets/Kits in request:\n`
    text += kits.map((kit) => `${kit.count} - ${kit.sku}`).join('\n')
  }

  text += `\n\nAll Props Requested:\n`
  text += valid.map((item) => `${item.count} - ${item.sku}`).join('\n')
  text += `\n\nSKUS:\n${skus}`

  if (invalid.length) {
    text += `\n\nNot Found:\n`
    text += invalid.map((item) => `${item.count} - ${item.postId}`).join('\n')
  }

  text += `\n\n${errorType ?? 'Unknown'} Error:\n${errorMessage}`

  return (
    <>
      <p className="twc-font-semibold">An error happened preventing the cart from submitting.</p>

      <p>
        Please refresh the page, close other open tabs for ACME, and/or restart your browser, and
        then try re-submitting.
      </p>

      <p>
        If you are still unable to submit without error, please email us directly at{' '}
        <a href={`${mailto}?subject=${encodeURIComponent(subject)}`}>PROPS@ACMEBROOKLYN.com</a>.{' '}
        <strong>Please copy the below into your message:</strong>
      </p>

      <Button
        size="small"
        className={`twc-mb-4 ${
          isCopied === false
            ? '!twc-text-red-800 !twc-bg-red-100'
            : isCopied === true
            ? '!twc-text-emerald-800 !twc-bg-emerald-100'
            : 'twc-text-inherit'
        }`}
        variant="secondary"
        onClick={() => handleCopyToClipboard(text)}
      >
        Copy message {isCopied ? <Check /> : isCopied === false ? <X /> : <CopySimple />}
      </Button>

      <textarea
        value={text}
        readOnly
        style={{ width: '100%', fontSize: '0.875rem', minHeight: 290 }}
      />

      <pre>
        {errorType ?? 'Unknown'} Error:
        <br />
        {errorMessage}
      </pre>
    </>
  )
}
